/**
 * Runs `task` for every item, at most `limit()` at a time, until all have run or `stop()` holds.
 * The limit is read again before each start, so it may change while the items run. After a task
 * fails, no further task starts; the call rejects with that failure once the running ones settled.
 */
export async function eachLimited<T>(
  items: Iterable<T>,
  limit: () => number,
  task: (item: T) => Promise<void>,
  stop: () => boolean = () => false,
): Promise<void> {
  const running = new Set<Promise<void>>()
  let failure: { error: unknown } | undefined
  for (const item of items) {
    while (!failure && running.size >= Math.max(1, limit())) await Promise.race(running)
    if (failure || stop()) break
    const run: Promise<void> = task(item)
      .catch((error: unknown) => void (failure ??= { error }))
      .finally(() => running.delete(run))
    running.add(run)
  }
  await Promise.all(running)
  if (failure) throw failure.error
}

/** At most `size` holders at a time; the others wait in the order they asked. */
export class Slots {
  private free: number
  private readonly waiting: (() => void)[] = []

  constructor(size: number) {
    this.free = size
  }

  async take(): Promise<void> {
    if (this.free > 0) {
      this.free--
      return
    }
    await new Promise<void>((resolve) => this.waiting.push(resolve))
  }

  give(): void {
    const next = this.waiting.shift()
    if (next) next()
    else this.free++
  }
}
